// src/jornada/finalizarJornada.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { jornadaStore } from "../data/jornadaStore";

export const FinalizarJornada = ({
  jornada,
  onJornadaActualizada,
  onSuccess,
  onError,
  uiLocked = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [centroCoords, setCentroCoords] = useState(null); // { lat, lon }
  const [centroNombre, setCentroNombre] = useState("");
  const limpiarMensajeTimeout = useRef(null);
  const clickedRef = useRef(false); // anti-doble click

  const flash = (msg, ms = 4500) => {
    setMensaje(msg);
    if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    limpiarMensajeTimeout.current = setTimeout(() => setMensaje(""), ms);
  };

  useEffect(() => {
    return () => {
      if (limpiarMensajeTimeout.current) clearTimeout(limpiarMensajeTimeout.current);
    };
  }, []);

  // Normaliza campos de la jornada que viene del padre (puede ser “local:*” si nació offline)
  const info = useMemo(() => {
    if (!jornada) return null;

    const latCentro =
      jornada?.centro?.latitud ??
      jornada?.centro_latitud ??
      jornada?.latitud_centro ??
      jornada?.centroLat;

    const lonCentro =
      jornada?.centro?.longitud ??
      jornada?.centro_longitud ??
      jornada?.longitud_centro ??
      jornada?.centroLon;

    return {
      id: jornada.id,
      esLocal: String(jornada.id || "").startsWith("local"),
      centroId: jornada.centro_id ?? jornada?.centro?.id ?? null,
      centro:
        jornada?.centro?.nombre ??
        jornada?.centro_nombre ??
        "",
      inicio:
        jornada.fecha_inicio ??
        jornada.inicio ??
        jornada.created_at ??
        null,
      latCentro: latCentro != null ? Number(latCentro) : null,
      lonCentro: lonCentro != null ? Number(lonCentro) : null,
    };
  }, [jornada]);

  // Completa coords/nombre del centro usando la store (offline-first) si faltan
  useEffect(() => {
    let alive = true;

    const hydrateCentro = async () => {
      if (!info?.id) return;

      // Si ya venían coords/nombre, úsalo tal cual
      if (info.latCentro != null && info.lonCentro != null) {
        if (alive) setCentroCoords({ lat: info.latCentro, lon: info.lonCentro });
        if (info.centro && info.centro.trim() !== "") {
          if (alive) setCentroNombre(info.centro);
        }
        return;
      }

      if (!info.centroId) return;

      // Intenta resolver desde la caché de centros (store.getCentros -> offline-first)
      try {
        const centros = await jornadaStore.getCentros();
        const c = Array.isArray(centros)
          ? centros.find((z) => Number(z.id) === Number(info.centroId))
          : null;

        if (c) {
          if (c.latitud != null && c.longitud != null) {
            if (alive) setCentroCoords({ lat: Number(c.latitud), lon: Number(c.longitud) });
          }
          if (c.nombre && !info.centro) {
            if (alive) setCentroNombre(c.nombre);
          }
        }
      } catch (e) {
        // Si falla en offline no pasa nada: podremos cerrar sin validar distancia
        console.warn("[FinalizarJornada] getCentros:", e?.message);
      }
    };

    hydrateCentro();
    return () => { alive = false; };
  }, [info?.id, info?.centroId, info?.latCentro, info?.lonCentro, info?.centro]);

  if (!info?.id) {
    return (
      <div className="jornada-activa">
        <h2 className="centrar-titulo">Jornada Activa</h2>
        <p>No hay ninguna jornada activa.</p>
      </div>
    );
  }

  const distanciaMetros = (lat1, lon1, lat2, lon2) => {
    const R = 6371000; const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1); const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const displayCentro =
    (info.centro && info.centro.trim() !== "")
      ? info.centro
      : (centroNombre || (info.centroId ? `Centro ${info.centroId}` : "Centro no disponible"));

  const finalizarConUbicacion = async () => {
    if (clickedRef.current || uiLocked) return;
    clickedRef.current = true;

    if (!navigator.geolocation) {
      clickedRef.current = false;
      onError?.("Tu navegador no soporta geolocalización.");
      return;
    }
    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const latFin = pos.coords.latitude;
          const lonFin = pos.coords.longitude;

          let metros = 0;
          let enRango = true;
          if (centroCoords && centroCoords.lat != null && centroCoords.lon != null) {
            metros = distanciaMetros(latFin, lonFin, centroCoords.lat, centroCoords.lon);
            // el umbral real vive en la lógica (store / backend); aquí sólo lo usamos de UX
            const UMBRAL_M = 100; // opcional: lee de config si tienes
            enRango = metros <= UMBRAL_M;
            if (!enRango) {
              const km = (metros / 1000).toFixed(2);
              const ok = window.confirm(`Estás a ${km} km del centro. ¿Deseas finalizar igualmente?`);
              if (!ok) { setLoading(false); clickedRef.current = false; return; }
            }
          }

          // ✅ Delega en la store: decide si es jornada local u online y resuelve endpoints/outbox
          const result = await jornadaStore.finish({
            latitud: latFin,
            longitud: lonFin,
            enRango,
            metros,
          });

          if (result?.status === "noop") {
            onError?.("No hay ninguna jornada activa o ya está pendiente de envío.");
          } else if (result?.status === "online") {
            onSuccess?.("Jornada finalizada correctamente.");
          } else {
            onSuccess?.("Sin conexión: cierre guardado y se enviará automáticamente.");
          }

          await onJornadaActualizada?.();
        } catch (e) {
          console.error(e);
          onError?.("No se pudo finalizar la jornada.");
        } finally {
          setLoading(false);
          clickedRef.current = false;
        }
      },
      (err) => {
        console.error(err);
        onError?.("No se pudo obtener la ubicación al cerrar.");
        setLoading(false);
        clickedRef.current = false;
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const cancelarJornada = async () => {
    if (clickedRef.current || uiLocked) return;
    clickedRef.current = true;

    try {
      setLoading(true);
      const result = await jornadaStore.cancel();

      if (result?.status === "noop") {
        onError?.("No hay ninguna jornada activa o ya está pendiente de envío.");
      } else if (result?.status === "online") {
        onSuccess?.("Jornada cancelada correctamente.");
      } else {
        onSuccess?.("Sin conexión: cancelación guardada y se enviará automáticamente.");
      }

      await onJornadaActualizada?.();
    } catch (e) {
      console.error(e);
      onError?.("No se pudo cancelar la jornada.");
    } finally {
      setLoading(false);
      clickedRef.current = false;
    }
  };

  const botonDeshabilitado = uiLocked || loading;

  return (
    <div className="jornada-activa">
      <h2 className="centrar-titulo">Jornada Activa</h2>

      {mensaje && <p className="rojo">{mensaje}</p>}

      <table className="table-listado">
        <thead>
          <tr>
            <th>Centro</th>
            <th>Inicio</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{displayCentro}</td>
            <td>{info.inicio ? new Date(info.inicio).toLocaleString() : "Fecha no disponible"}</td>
            <td>
              <button className="btn-login" onClick={finalizarConUbicacion} disabled={botonDeshabilitado}>
                {loading ? "Procesando..." : "Finalizar"}
              </button>
              <button
                className="btn-login"
                onClick={cancelarJornada}
                disabled={botonDeshabilitado}
                style={{ marginLeft: 8 }}
              >
                Cancelar
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
